import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UserPlus, CheckCircle, XCircle, Clock, Briefcase, FileText, Mail, Phone, Building2, Loader2, Copy, ChevronDown, ChevronUp, ClipboardCheck, MapPin, Shield, ShieldOff, KeyRound, Pencil, Save, X, ToggleLeft, ToggleRight, Calculator } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  approved: 'bg-green-100 text-green-700 border-green-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
};

const PROFESSION_ICONS = { CA: Briefcase, CS: FileText };

export default function AdminPartnerRequests() {
  const [requests, setRequests] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [tab, setTab] = useState('requests');
  const [approvedCreds, setApprovedCreds] = useState(null);
  const [resetCreds, setResetCreds] = useState(null);
  const [editingPartner, setEditingPartner] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [pinLookup, setPinLookup] = useState({});
  const [worksheetSummary, setWorksheetSummary] = useState({});
  const [expandedWorksheets, setExpandedWorksheets] = useState(null);

  const token = localStorage.getItem('ratnatraya_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [reqRes, partnerRes, wsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/partner-requests`, { headers }),
        fetch(`${API_URL}/api/admin/partners`, { headers }),
        fetch(`${API_URL}/api/admin/partner-worksheet-summary`, { headers }),
      ]);
      setRequests(await reqRes.json());
      setPartners(await partnerRes.json());
      const wsData = await wsRes.json();
      setWorksheetSummary(wsData || {});
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (requestId, status, password) => {
    setProcessing(p => ({ ...p, [requestId]: true }));
    try {
      const res = await fetch(`${API_URL}/api/admin/partner-requests/${requestId}`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ status, password: password || undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      if (status === 'approved') {
        const req = requests.find(r => r.id === requestId);
        setApprovedCreds({ name: req?.full_name, email: req?.email, password: data.temp_password });
        toast.success('Partner approved! Credentials email sent automatically.');
      } else {
        toast.success('Request rejected');
      }
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessing(p => ({ ...p, [requestId]: false }));
    }
  };

  const updateVerification = async (requestId, field, value) => {
    try {
      await fetch(`${API_URL}/api/admin/partner-requests/${requestId}/verification`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ [field]: value }),
      });
      setRequests(prev => prev.map(r => r.id === requestId ? { ...r, verification: { ...(r.verification || {}), [field]: value } } : r));
    } catch (err) {
      toast.error('Failed to update verification');
    }
  };

  const lookupPinCode = useCallback(async (pin, target, targetId) => {
    if (!pin || pin.length !== 6) return;
    setPinLookup(p => ({ ...p, [targetId]: { loading: true } }));
    try {
      const res = await fetch(`${API_URL}/api/pincode/${pin}`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Not found');
      setPinLookup(p => ({ ...p, [targetId]: { ...data, loading: false } }));
      if (target === 'request') {
        await fetch(`${API_URL}/api/admin/partner-requests/${targetId}/details`, {
          method: 'PATCH', headers,
          body: JSON.stringify({ pin_code: pin, city: data.city, state: data.state, district: data.district }),
        });
        setRequests(prev => prev.map(r => r.id === targetId ? { ...r, pin_code: pin, city: data.city, state: data.state, district: data.district } : r));
        toast.success(`Location: ${data.city}, ${data.state}`);
      } else if (target === 'partner') {
        setEditForm(f => ({ ...f, city: data.city, state: data.state, district: data.district }));
      }
    } catch (err) {
      setPinLookup(p => ({ ...p, [targetId]: { error: true, loading: false } }));
      toast.error(err.message || 'PIN code lookup failed');
    }
  }, [headers]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetPassword = async (partnerId) => {
    setProcessing(p => ({ ...p, [`reset_${partnerId}`]: true }));
    try {
      const res = await fetch(`${API_URL}/api/admin/partners/${partnerId}/reset-password`, {
        method: 'POST', headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setResetCreds({ partnerId, password: data.temp_password });
      toast.success('Password reset! New credentials emailed to partner.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessing(p => ({ ...p, [`reset_${partnerId}`]: false }));
    }
  };

  const togglePartnerStatus = async (partnerId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      await fetch(`${API_URL}/api/admin/partners/${partnerId}/status`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ status: newStatus }),
      });
      toast.success(`Partner ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const startEditPartner = (partner) => {
    setEditingPartner(partner.id);
    setEditForm({
      name: partner.name || '',
      email: partner.email || '',
      mobile: partner.mobile || '',
      firm_name: partner.firm_name || '',
      has_cop: partner.has_cop || false,
      pin_code: partner.pin_code || '',
      city: partner.city || '',
      state: partner.state || '',
      district: partner.district || '',
    });
  };

  const savePartnerEdit = async (partnerId) => {
    setProcessing(p => ({ ...p, [`edit_${partnerId}`]: true }));
    try {
      const res = await fetch(`${API_URL}/api/admin/partners/${partnerId}`, {
        method: 'PATCH', headers,
        body: JSON.stringify(editForm),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Failed'); }
      toast.success('Profile updated');
      setEditingPartner(null);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessing(p => ({ ...p, [`edit_${partnerId}`]: false }));
    }
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const activeCount = partners.filter(p => p.status === 'active').length;
  const inactiveCount = partners.filter(p => p.status !== 'active').length;

  if (loading) {
    return <div className="p-8 flex items-center justify-center min-h-[50vh]"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-partner-requests">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">R-PACE Partners</h1>
          <p className="text-slate-600 text-sm">Manage partner access, verification, and credentials</p>
        </div>
        <div className="flex items-center gap-3">
          {pendingCount > 0 && (
            <span className="flex items-center gap-1.5 bg-amber-100 text-amber-700 px-3 py-1.5 rounded-lg text-xs font-semibold">
              <Clock className="w-3.5 h-3.5" /> {pendingCount} pending
            </span>
          )}
          <span className="flex items-center gap-1.5 bg-green-100 text-green-700 px-3 py-1.5 rounded-lg text-xs font-semibold">
            <CheckCircle className="w-3.5 h-3.5" /> {activeCount} active
          </span>
          {inactiveCount > 0 && (
            <span className="flex items-center gap-1.5 bg-slate-100 text-slate-500 px-3 py-1.5 rounded-lg text-xs font-semibold">
              {inactiveCount} inactive
            </span>
          )}
        </div>
      </div>

      {/* Approved Credentials Card */}
      {approvedCreds && (
        <Card className="mb-6 border-2 border-green-300 bg-green-50" data-testid="credentials-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <h3 className="text-sm font-bold text-green-800">Partner Approved — Credentials Emailed</h3>
              </div>
              <button onClick={() => setApprovedCreds(null)} className="text-slate-400 hover:text-slate-600 text-lg leading-none" data-testid="dismiss-creds">&times;</button>
            </div>
            <div className="bg-white border border-green-200 rounded-lg p-4 space-y-2">
              <p className="text-sm text-slate-700"><span className="font-semibold text-slate-500 w-20 inline-block">Name:</span> {approvedCreds.name}</p>
              <p className="text-sm text-slate-700"><span className="font-semibold text-slate-500 w-20 inline-block">Email:</span> {approvedCreds.email}</p>
              <p className="text-sm text-slate-700"><span className="font-semibold text-slate-500 w-20 inline-block">Password:</span> <code className="bg-amber-50 border border-amber-200 px-2 py-0.5 rounded text-amber-800 font-mono text-sm">{approvedCreds.password}</code></p>
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" className="gap-1.5 text-xs" data-testid="copy-password-btn" onClick={() => { navigator.clipboard.writeText(approvedCreds.password); toast.success('Password copied!'); }}><Copy className="w-3.5 h-3.5" /> Copy Password</Button>
              <Button size="sm" variant="outline" className="gap-1.5 text-xs" data-testid="copy-all-btn" onClick={() => { navigator.clipboard.writeText(`R-PACE Login Credentials\nName: ${approvedCreds.name}\nEmail: ${approvedCreds.email}\nPassword: ${approvedCreds.password}\nLogin: ${window.location.origin}/r-pace-partner-login`); toast.success('All credentials copied!'); }}><Copy className="w-3.5 h-3.5" /> Copy All</Button>
            </div>
            <p className="text-[11px] text-green-600 mt-2">Credentials have been auto-emailed to the partner.</p>
          </CardContent>
        </Card>
      )}

      {/* Reset Password Credentials Card */}
      {resetCreds && (
        <Card className="mb-6 border-2 border-blue-300 bg-blue-50" data-testid="reset-credentials-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-blue-600" />
                <h3 className="text-sm font-bold text-blue-800">Password Reset — New Credentials Emailed</h3>
              </div>
              <button onClick={() => setResetCreds(null)} className="text-slate-400 hover:text-slate-600 text-lg leading-none">&times;</button>
            </div>
            <div className="bg-white border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-slate-700"><span className="font-semibold text-slate-500 w-24 inline-block">New Password:</span> <code className="bg-amber-50 border border-amber-200 px-2 py-0.5 rounded text-amber-800 font-mono text-sm">{resetCreds.password}</code></p>
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => { navigator.clipboard.writeText(resetCreds.password); toast.success('New password copied!'); }}><Copy className="w-3.5 h-3.5" /> Copy Password</Button>
            </div>
            <p className="text-[11px] text-blue-600 mt-2">New credentials have been auto-emailed to the partner.</p>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-100 rounded-lg p-1 w-fit" data-testid="partner-tabs">
        <button onClick={() => setTab('requests')} className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'requests' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          Access Requests {pendingCount > 0 && <span className="ml-1 bg-amber-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{pendingCount}</span>}
        </button>
        <button onClick={() => setTab('partners')} className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'partners' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          Partners ({partners.length})
        </button>
      </div>

      {/* Requests Tab */}
      {tab === 'requests' && (
        <div className="space-y-3">
          {requests.length === 0 ? (
            <Card><CardContent className="p-12 text-center"><UserPlus className="w-10 h-10 mx-auto mb-3 text-slate-300" /><p className="text-slate-500">No access requests yet</p></CardContent></Card>
          ) : (
            requests.map(req => {
              const ProfIcon = PROFESSION_ICONS[req.profession] || Briefcase;
              const isExpanded = expandedId === req.id;
              const verification = req.verification || {};
              const pin = pinLookup[req.id] || {};
              return (
                <Card key={req.id} className="overflow-hidden" data-testid={`request-${req.id}`}>
                  <div className="p-4 md:p-5 cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => setExpandedId(isExpanded ? null : req.id)}>
                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center shrink-0">
                          <ProfIcon className="w-5 h-5 text-[#1e3a5f]" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 truncate">{req.full_name}</p>
                          <p className="text-sm text-slate-500 truncate">{req.profession} · {req.firm_name}{req.city ? ` · ${req.city}` : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${STATUS_COLORS[req.status]}`}>{req.status.toUpperCase()}</span>
                        <span className="text-xs text-slate-400">{new Date(req.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-slate-100 p-4 md:p-5 bg-slate-50 space-y-4">
                      {/* Contact details */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="flex items-start gap-2.5">
                          <Mail className="w-4 h-4 text-slate-400 mt-0.5" />
                          <div><p className="text-[10px] text-slate-400 uppercase">Email</p><p className="text-sm text-slate-800">{req.email}</p></div>
                        </div>
                        <div className="flex items-start gap-2.5">
                          <Phone className="w-4 h-4 text-slate-400 mt-0.5" />
                          <div><p className="text-[10px] text-slate-400 uppercase">Mobile</p><p className="text-sm text-slate-800">{req.mobile}</p></div>
                        </div>
                        <div className="flex items-start gap-2.5">
                          <Building2 className="w-4 h-4 text-slate-400 mt-0.5" />
                          <div><p className="text-[10px] text-slate-400 uppercase">Firm</p><p className="text-sm text-slate-800">{req.firm_name}</p></div>
                        </div>
                        {req.membership_number && (
                          <div className="flex items-start gap-2.5">
                            <ClipboardCheck className="w-4 h-4 text-slate-400 mt-0.5" />
                            <div><p className="text-[10px] text-slate-400 uppercase">Membership No.</p><p className="text-sm text-slate-800 font-mono">{req.membership_number}</p></div>
                          </div>
                        )}
                      </div>

                      {/* PIN Code + City (editable before approval) */}
                      {req.status === 'pending' && (
                        <div className="flex items-center gap-3 bg-white p-3 rounded-lg border border-slate-200">
                          <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
                          <div className="flex items-center gap-2 flex-wrap">
                            <Input
                              placeholder="PIN Code"
                              className="w-28 h-8 text-sm"
                              maxLength={6}
                              defaultValue={req.pin_code || ''}
                              data-testid={`pin-input-${req.id}`}
                              onChange={(e) => {
                                const v = e.target.value.replace(/\D/g, '');
                                e.target.value = v;
                                if (v.length === 6) lookupPinCode(v, 'request', req.id);
                              }}
                            />
                            {pin.loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                            {req.city && <span className="text-sm text-slate-700 font-medium">{req.city}, {req.state}</span>}
                            {pin.error && <span className="text-xs text-red-500">PIN not found</span>}
                          </div>
                        </div>
                      )}
                      {req.status !== 'pending' && req.city && (
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <span>{req.city}, {req.state} ({req.pin_code})</span>
                        </div>
                      )}

                      {/* Verification checklist (only for pending) */}
                      {req.status === 'pending' && (
                        <div className="bg-white p-3 rounded-lg border border-slate-200">
                          <p className="text-[10px] text-slate-400 uppercase mb-2 font-semibold">Verification Checklist</p>
                          <div className="flex flex-wrap gap-3">
                            {[
                              { key: 'icai_icsi_checked', label: 'ICAI/ICSI Directory Verified' },
                              { key: 'pan_received', label: 'PAN Card Received' },
                              { key: 'video_call_done', label: 'Video Call Done' },
                            ].map(({ key, label }) => (
                              <label key={key} className="flex items-center gap-2 cursor-pointer select-none" data-testid={`verify-${key}-${req.id}`}>
                                <input
                                  type="checkbox"
                                  checked={verification[key] || false}
                                  onChange={(e) => updateVerification(req.id, key, e.target.checked)}
                                  className="w-4 h-4 rounded border-slate-300 text-[#1e3a5f] focus:ring-[#1e3a5f]"
                                />
                                <span className={`text-xs ${verification[key] ? 'text-green-700 font-medium' : 'text-slate-500'}`}>{label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Action buttons */}
                      {req.status === 'pending' && (
                        <div className="flex items-center gap-3 pt-2">
                          <Button size="sm" className="bg-green-600 hover:bg-green-700 gap-1.5" onClick={() => handleAction(req.id, 'approved')} disabled={processing[req.id]} data-testid={`approve-${req.id}`}>
                            {processing[req.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />} Approve
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 gap-1.5" onClick={() => handleAction(req.id, 'rejected')} disabled={processing[req.id]} data-testid={`reject-${req.id}`}>
                            <XCircle className="w-3.5 h-3.5" /> Reject
                          </Button>
                        </div>
                      )}

                      {req.status === 'approved' && (
                        <p className="text-xs text-green-600 bg-green-50 border border-green-200 rounded-lg px-3 py-2">Partner account created. Credentials were emailed on approval.</p>
                      )}
                    </div>
                  )}
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* Partners Tab */}
      {tab === 'partners' && (
        <div className="space-y-3">
          {partners.length === 0 ? (
            <Card><CardContent className="p-12 text-center"><UserPlus className="w-10 h-10 mx-auto mb-3 text-slate-300" /><p className="text-slate-500">No approved partners yet</p></CardContent></Card>
          ) : (
            partners.map(partner => {
              const ProfIcon = PROFESSION_ICONS[partner.profession] || Briefcase;
              const isEditing = editingPartner === partner.id;
              const isInactive = partner.status !== 'active';
              return (
                <Card key={partner.id} className={`overflow-hidden ${isInactive ? 'opacity-60 border-slate-300' : ''}`} data-testid={`partner-${partner.id}`}>
                  <CardContent className="p-4 md:p-5">
                    {isEditing ? (
                      /* Edit mode */
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-bold text-slate-800">Edit Partner Profile</h3>
                          <div className="flex gap-2">
                            <Button size="sm" className="h-7 text-xs gap-1 bg-[#1e3a5f]" onClick={() => savePartnerEdit(partner.id)} disabled={processing[`edit_${partner.id}`]} data-testid={`save-edit-${partner.id}`}>
                              {processing[`edit_${partner.id}`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setEditingPartner(null)}>
                              <X className="w-3 h-3" /> Cancel
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className="text-[10px] text-slate-400 uppercase">Name</label>
                            <Input className="h-8 text-sm mt-1" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-400 uppercase">Email</label>
                            <Input className="h-8 text-sm mt-1" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-400 uppercase">Mobile</label>
                            <Input className="h-8 text-sm mt-1" value={editForm.mobile} onChange={e => setEditForm(f => ({ ...f, mobile: e.target.value }))} />
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-400 uppercase">Firm Name</label>
                            <Input className="h-8 text-sm mt-1" value={editForm.firm_name} onChange={e => setEditForm(f => ({ ...f, firm_name: e.target.value }))} />
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-400 uppercase">PIN Code</label>
                            <Input className="h-8 text-sm mt-1" placeholder="6-digit PIN" maxLength={6} value={editForm.pin_code}
                              onChange={e => {
                                const v = e.target.value.replace(/\D/g, '');
                                setEditForm(f => ({ ...f, pin_code: v }));
                                if (v.length === 6) lookupPinCode(v, 'partner', partner.id);
                              }}
                            />
                            {editForm.city && <p className="text-xs text-slate-500 mt-1">{editForm.city}, {editForm.state}</p>}
                          </div>
                          <div className="flex items-end">
                            <label className="flex items-center gap-2 cursor-pointer select-none pb-1">
                              <input type="checkbox" checked={editForm.has_cop || false} onChange={e => setEditForm(f => ({ ...f, has_cop: e.target.checked }))} className="w-4 h-4 rounded border-slate-300 text-[#1e3a5f]" />
                              <span className="text-sm text-slate-700">Certificate of Practice (COP)</span>
                            </label>
                          </div>
                        </div>
                        <p className="text-[10px] text-slate-400">Membership number ({partner.membership_number}) cannot be changed.</p>
                      </div>
                    ) : (
                      /* View mode */
                      <div className="flex flex-col gap-3">
                        <div className="flex flex-col md:flex-row md:items-center gap-3">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isInactive ? 'bg-slate-100' : 'bg-green-100'}`}>
                              <ProfIcon className={`w-5 h-5 ${isInactive ? 'text-slate-400' : 'text-green-700'}`} />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-900 truncate">
                                {partner.name}
                                {partner.has_cop && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#1e3a5f] text-white" data-testid={`cop-badge-${partner.id}`}>COP</span>}
                              </p>
                              <p className="text-sm text-slate-500 truncate">
                                {partner.profession} · {partner.firm_name}
                                {partner.city ? ` · ${partner.city}, ${partner.state}` : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {(worksheetSummary[partner.id]?.count > 0) && (
                              <button
                                onClick={() => setExpandedWorksheets(expandedWorksheets === partner.id ? null : partner.id)}
                                className="flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 transition-colors"
                                data-testid={`worksheet-count-${partner.id}`}
                              >
                                <Calculator className="w-3 h-3" />
                                {worksheetSummary[partner.id].count} assessment{worksheetSummary[partner.id].count !== 1 ? 's' : ''}
                              </button>
                            )}
                            <span className="text-xs text-slate-500">{partner.email}</span>
                            <span className="text-xs text-slate-400">{partner.mobile}</span>
                            {partner.membership_number && <span className="text-[10px] font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{partner.membership_number}</span>}
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${isInactive ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-700'}`}>
                              {partner.status === 'active' ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>

                        {/* Worksheet List (expandable) */}
                        {expandedWorksheets === partner.id && worksheetSummary[partner.id] && (
                          <div className="bg-teal-50/50 border border-teal-200 rounded-lg p-3" data-testid={`worksheet-list-${partner.id}`}>
                            <p className="text-[10px] text-teal-700 font-bold uppercase mb-2">
                              Assessments by {partner.name} ({worksheetSummary[partner.id].count})
                            </p>
                            <div className="space-y-1.5 max-h-48 overflow-y-auto">
                              {worksheetSummary[partner.id].worksheets.map(ws => {
                                const statusColors = {
                                  completed: 'bg-green-100 text-green-700',
                                  in_progress: 'bg-amber-100 text-amber-700',
                                  draft: 'bg-slate-100 text-slate-500',
                                };
                                const statusLabel = ws.worksheet_status === 'completed' ? 'Completed' : ws.worksheet_status === 'in_progress' ? 'In Progress' : 'Started';
                                return (
                                  <div key={ws.id} className="flex items-center justify-between bg-white rounded px-3 py-2 border border-teal-100" data-testid={`ws-${ws.id}`}>
                                    <div className="flex items-center gap-2 min-w-0">
                                      <Calculator className="w-3.5 h-3.5 text-teal-500 shrink-0" />
                                      <span className="text-sm text-slate-800 truncate">{ws.company_name || '(Untitled)'}</span>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${statusColors[ws.worksheet_status] || statusColors.draft}`}>
                                        {statusLabel}
                                      </span>
                                      <span className="text-[10px] text-slate-400">
                                        {ws.created_at ? new Date(ws.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ''}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Action buttons */}
                        <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
                          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => startEditPartner(partner)} data-testid={`edit-partner-${partner.id}`}>
                            <Pencil className="w-3 h-3" /> Edit Profile
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-blue-600 border-blue-200 hover:bg-blue-50" onClick={() => resetPassword(partner.id)} disabled={processing[`reset_${partner.id}`]} data-testid={`reset-pwd-${partner.id}`}>
                            {processing[`reset_${partner.id}`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <KeyRound className="w-3 h-3" />} Reset Password
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className={`h-7 text-xs gap-1 ${isInactive ? 'text-green-600 border-green-200 hover:bg-green-50' : 'text-red-600 border-red-200 hover:bg-red-50'}`}
                            onClick={() => togglePartnerStatus(partner.id, partner.status)}
                            data-testid={`toggle-status-${partner.id}`}
                          >
                            {isInactive ? <ToggleRight className="w-3 h-3" /> : <ToggleLeft className="w-3 h-3" />}
                            {isInactive ? 'Activate' : 'Deactivate'}
                          </Button>
                        </div>

                        {/* Reset password display */}
                        {resetCreds?.partnerId === partner.id && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 flex items-center gap-2">
                            <KeyRound className="w-4 h-4 text-blue-500" />
                            <span className="text-xs text-blue-700">New password: <code className="bg-white px-2 py-0.5 rounded font-mono text-amber-800 border border-amber-200">{resetCreds.password}</code></span>
                            <button className="text-xs text-blue-600 underline ml-2" onClick={() => { navigator.clipboard.writeText(resetCreds.password); toast.success('Copied!'); }}>Copy</button>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
